import { tg } from './telegram';
import { isKeyboardEditor, keyboardViewport } from './keyboard-geometry';
import { watchFocusedFieldScroll } from './focused-field-scroll';

/** Native keyboards have no cross-platform event: require an editable focus AND
 * a substantial viewport contraction. Retain the closed baseline during blur /
 * keyboard animation; Telegram may shrink viewportStableHeight as well. */
export function watchKeyboard(): () => void {
  const viewport = window.visualViewport;
  const app = tg();
  const visualHeight = () => viewport && viewport.scale === 1 ? viewport.height : window.innerHeight;
  const telegramHeight = () => app?.viewportHeight || window.innerHeight;
  let closedVisual = visualHeight();
  let closedTelegram = telegramHeight();
  let width = window.innerWidth;
  const closedByWidth = new Map<number, { visual: number; telegram: number }>();
  let open = false;
  let frame = 0;
  let navFrame = 0;
  let navUntil = 0;
  let navCorrection = 0;
  const stopReveal = watchFocusedFieldScroll();

  const editable = () => isKeyboardEditor(document.activeElement);
  // Fixed bottom:0 follows a resized WebView, not the closed physical bottom.
  // Keep a separate layout anchor: keyboard detection's threshold is too late
  // for the first animation samples. Never change opacity, inert or form state.
  let navHeight = innerHeight;
  let navWidth = innerWidth;
  let navHeld = false;
  let navKeyboardContracted = false;
  const navClosed = new Map<number, number>([[navWidth, navHeight]]);
  const anchorNavigation = () => {
    const focused = editable();
    if (Math.abs(innerWidth - navWidth) > 80) {
      // Measured destination first; first open rotation has only a dimension-
      // swap estimate. Native chrome/safe-area changes still need device proof.
      navHeight = navHeld ? navClosed.get(innerWidth) ?? navWidth : innerHeight;
      navWidth = innerWidth;
    }
    if (focused) navHeld = true;
    const visibleHeight = Math.min(innerHeight, visualHeight(), telegramHeight());
    if ((focused || open) && navHeight - visibleHeight > Math.max(120, navHeight * 0.18)) navKeyboardContracted = true;
    if (visibleHeight >= navHeight - 1) navKeyboardContracted = false;
    // A recovered keyboard session must not leave an eternal hold after a
    // later small window resize and blur/route exit. During a known keyboard
    // contraction, retain the closed edge through blur and partial recovery.
    if (!focused && !navKeyboardContracted) navHeld = false;
    if (!navHeld || innerHeight >= navHeight) {
      navHeight = innerHeight;
      navClosed.set(navWidth, navHeight);
      if (!focused) navHeld = false;
    }
    // Native experiment: ordinary browsing delegates fixed positioning to CSS.
    // Do not integrate WebKit's asynchronous fixed-layer rect during a drag.
    document.documentElement.toggleAttribute('data-nav-held', navHeld);
    if (!navHeld) {
      navCorrection = 0;
      document.documentElement.style.removeProperty('--nav-viewport-bottom');
      return;
    }
    const pan = viewport?.scale === 1 ? viewport.offsetTop : 0;
    const target = navHeight + pan;
    const root = document.documentElement;
    const write = (value: number) => {
      const text = `${value}px`;
      if (root.style.getPropertyValue('--nav-viewport-bottom') !== text) root.style.setProperty('--nav-viewport-bottom', text);
    };
    write(target + navCorrection);
    const nav = document.querySelector<HTMLElement>('.nav');
    if (nav) {
      // A translated containing block/document pan moves the actual rect even
      // though the stored closed edge is unchanged. Correct measured geometry,
      // not opacity or keyboard state. Native compositor-only movement remains
      // outside this DOM contract and requires device acceptance.
      const error = target - nav.getBoundingClientRect().bottom;
      if (Math.abs(error) > 0.5) { navCorrection += error; write(target + navCorrection); }
    } else navCorrection = 0;
  };
  const update = () => {
    const visual = visualHeight();
    const telegram = telegramHeight();
    const focused = editable();
    if (Math.abs(window.innerWidth - width) > 80) {
      // A rotation swaps the closed dimensions, not the keyboard-shrunken ones.
      // Prefer a measured baseline at the destination width; on its first visit
      // the previous width estimates the new closed height. Never carry the old
      // portrait height into landscape, or closing there would stay hidden.
      const rotated = Math.abs(window.innerWidth - closedVisual) <= 80 ||
        Math.abs(window.innerWidth - closedTelegram) <= 80;
      if (open && rotated) {
        const closed = closedByWidth.get(window.innerWidth);
        closedVisual = closed?.visual ?? width;
        closedTelegram = closed?.telegram ?? width;
      } else {
        // Ordinary window resizing / rotation without a keyboard is not evidence.
        closedVisual = visual;
        closedTelegram = telegram;
        open = false;
      }
    }
    width = window.innerWidth;
    const contracted = closedVisual - visual > Math.max(120, closedVisual * 0.18) ||
      closedTelegram - telegram > Math.max(120, closedTelegram * 0.18);
    open = contracted && (focused || open);
    document.documentElement.toggleAttribute('data-keyboard-open', open);
    if (!open && !focused) {
      closedVisual = visual;
      closedTelegram = telegram;
    } else if (!open) {
      closedVisual = Math.max(closedVisual, visual);
      closedTelegram = Math.max(closedTelegram, telegram);
    }
    if (!open) closedByWidth.set(width, { visual: closedVisual, telegram: closedTelegram });
    const geometry = keyboardViewport();
    const root = document.documentElement;
    // Keep existing page flow/tab padding stable. Only a trailing scroll reserve
    // is added; routed forms are never resized, translated, or eased as a page.
    // A negative visual offset is native pan/rubber-band, not extra keyboard
    // occlusion. Feeding it into document height grows the scroll range during
    // inertia and causes a scroll/offset/reserve positive feedback loop.
    const clearance = Math.max(0, innerHeight - geometry.height - Math.max(0, geometry.top));
    root.style.setProperty('--keyboard-clearance', `${open ? clearance : 0}px`);
    root.style.setProperty('--keyboard-bottom', `${geometry.bottom}px`);
    root.style.setProperty('--keyboard-top', `${geometry.top}px`);
    // Navigation stays at its normal bottom position. The OS keyboard occludes
    // it; only modal ownership may make its ancestors inert.
    // One geometry owner, also used by the combobox. No scroll event listener
    // fights a user's drag or a native browser pan.
    document.dispatchEvent(new Event('app-keyboard-geometry'));

  };
  const settleNavigation = (now: number) => {
    navFrame = 0;
    anchorNavigation();
    if (now < navUntil && !document.hidden) navFrame = requestAnimationFrame(settleNavigation);
  };
  const scheduleNavigation = () => {
    anchorNavigation();
    if (!navHeld) { cancelAnimationFrame(navFrame); navFrame = 0; return; }
    // Bounded reconciliation for late offset/rect reads after a native callback.
    // This does not hold keyboard state or wait indefinitely for focus to blur.
    navUntil = performance.now() + 488;
    if (!navFrame) navFrame = requestAnimationFrame(settleNavigation);
  };
  const schedule = () => {
    scheduleNavigation();
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  };
  const stopFrames = () => { cancelAnimationFrame(navFrame); navFrame = 0; };
  const observer = new ResizeObserver(scheduleNavigation);
  const appRoot = document.getElementById('root');
  if (appRoot) observer.observe(appRoot);
  window.addEventListener('scroll', scheduleNavigation, { passive: true });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('pagehide', stopFrames);
  viewport?.addEventListener('scroll', schedule);
  viewport?.addEventListener('resize', schedule);
  window.addEventListener('resize', schedule);
  document.addEventListener('focusin', schedule);
  document.addEventListener('focusout', schedule);
  app?.onEvent?.('viewportChanged', schedule);
  anchorNavigation();
  update();
  return () => {
    stopReveal();
    stopFrames();
    observer.disconnect();
    window.removeEventListener('scroll', scheduleNavigation);
    window.removeEventListener('pageshow', schedule);
    window.removeEventListener('pagehide', stopFrames);
    cancelAnimationFrame(frame);
    viewport?.removeEventListener('scroll', schedule);
    for (const name of ['--keyboard-clearance','--keyboard-bottom','--keyboard-top','--nav-viewport-bottom']) document.documentElement.style.removeProperty(name);
    viewport?.removeEventListener('resize', schedule);
    window.removeEventListener('resize', schedule);
    document.removeEventListener('focusin', schedule);
    document.removeEventListener('focusout', schedule);
    app?.offEvent?.('viewportChanged', schedule);
    document.documentElement.removeAttribute('data-nav-held');
    document.documentElement.removeAttribute('data-keyboard-open');
  };
}
